import { DateTime } from "luxon";

const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;

let _patrolId = null;
export const setPatrolId = (id) => { _patrolId = id; };
export const getPatrolId = () => _patrolId;

// MARK: getUserProfile (core — no patrolAuth needed)
export const getUserProfile = async (uid, token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/core/users/${ uid }`, payload);

        if (response.ok) {
            return await response.json();
        } else {
            return null;
        }
    } catch (error) {
        console.error('getUserProfile:', error);

        return null;
    }
};

// MARK: validateRegistrationCode
export const validateRegistrationCode = async (identifier, code) => {
    try {
        const payload = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ identifier, code })
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/notify/verify/validate`, payload);
        const responseText = await response.text();
        const json = responseText.trim().length > 0 && JSON.parse(responseText);

        if (response.ok) {
            return true;
        } else {
            return { error: json.reason || 'Failed to validate registration code.' };
        }
    } catch (error) {
        console.error('validateRegistrationCode:', error);

        return { error: error.message };
    }
};

// MARK: getCertifications
export const getCertifications = async (token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/certify/certifications`, payload);
        const responseText = await response.text();
        const json = responseText.trim().length > 0 && JSON.parse(responseText);

        if (response.ok) {
            return json;
        } else {
            return { error: json.error };
        }
    } catch (error) {
        console.error('getCertifications:', error);

        return { error: error.message };
    }
};

// MARK: getDuties
export const getDuties = async (id, token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/manage/duties/patroller/${ id }`, payload);
        const responseText = await response.text();
        const json = response.ok && responseText.trim().length > 0 && JSON.parse(responseText);

        if (Array.isArray(json)) {
            return json.sort((a, b) => a.shift.start - b.shift.start);
        } else {
            return;
        };
    } catch (error) {
        console.error('getDuties:', error);

        return error;
    }
};

// MARK: clockInOut
export const clockInOut = async (patroller, shift, action, token) => {
    try {
        const payload = {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'Content-Type': 'application/json',
                'x-patrol-id': _patrolId
            },
            body: JSON.stringify({ patroller: patroller, shift: shift })
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/manage/timelogs/patroller/${ patroller }/shift/${ shift }/action/${ action }`, payload);

        if (response.ok) {
            return { ok: true, status: response.status };
        }

        // Single attempt, no retry. Older servers may return no/non-JSON body; that is fine.
        let json = {};

        try {
            const responseText = await response.text();

            json = responseText.trim().length > 0 ? JSON.parse(responseText) : {};
        } catch {
            json = {};
        }

        return {
            ok: false,
            status: response.status,
            error: json?.error,
            reason: json?.reason,
            opensAt: json?.opensAt
        };
    } catch (error) {
        console.error('clockInOut:', error);

        return { ok: false, status: 0, error: 'network', reason: undefined, opensAt: undefined };
    }
};

// MARK: getPatroller
export const getPatroller = async (id, token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/manage/patrollers/${ id }`, payload);
        const responseText = await response.text();
        const json = responseText.trim().length > 0 && JSON.parse(responseText);

        if (response.ok) {
            return json;
        } else if (response.status === 401) {
            return { error: 'Your session has expired or is invalid. Please log out and log back in to continue.' };
        } else {
            return { error: json.error || 'Failed to fetch patroller.' };
        }
    } catch (error) {
        return error;
    }
};

// MARK: getShift
export const getShift = async (id, token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/manage/shifts/${ id }`, payload);
        const responseText = await response.text();
        const json = response.status !== 404 && responseText.trim().length > 0 && JSON.parse(responseText);

        if (response.ok) {
            return json;
        } else if (json.error) {
            return { error: json.error || 'Failed to fetch shift.' };
        } else {
            return;
        }
    } catch (error) {
        console.error('getShift:', error);

        return error;
    }
};

// MARK: getShifts
// `since` (epoch ms) is the lower bound on shift start; defaults to the start of today (local).
// The server ignores date query params on this endpoint, so the bound is applied client-side.
export const getShifts = async (token, since = DateTime.now().startOf('day').toMillis()) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/manage/shifts`, payload);
        const responseText = await response.text();
        const json = responseText.trim().length > 0 && JSON.parse(responseText);

        if (Array.isArray(json) && json.length > 0) {
            const filtered = json.filter(s => s && s.event && s.start >= since).sort((a, b) => a.start - b.start);

            return filtered;
        } else if (json.error) {
            return { error: json.error || 'Failed to fetch shifts.' };
        } else { // 404
            return [];
        }
    } catch (error) {
        console.error('getShifts:', error);

        return error;
    }
};

// MARK: clearDutyAssignment
export const clearDutyAssignment = async (pid, sid, rid, token) => {
    try {
        const payload = {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'Content-Type': 'application/json',
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/manage/patrollers/${ pid }/shift/${ sid }`, payload);
        const responseText = await response.text();
        const json = responseText.trim().length > 0 && JSON.parse(responseText);

        if (response.ok) {
            return true;
        } else {
            return { error: json.error || 'Failed to clear duty assignment.' };
        }
    } catch (error) {
        console.error('clearDutyAssignment:', error);

        return error;
    }
};

//MARK: setDutyAssignment
export const setDutyAssignment = async (pid, sid, rid, token) => {
    try {
        const payload = {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'Content-Type': 'application/json',
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/manage/patrollers/${ pid }/shift/${ sid }/role/${ rid }`, payload);
        const responseText = await response.text();
        const json = responseText.trim().length > 0 && JSON.parse(responseText);

        if (!response.ok) {
            return { error: json.error || 'Failed to set duty assignment.', ...(json.opensAt !== undefined && { opensAt: json.opensAt }) };
        }

        return response.ok;
    } catch (error) {
        console.error('setDutyAssignment:', error);

        return error;
    }
};

// MARK: getRadio
export const getRadio = async (id, token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/manage/radios/${ id }`, payload);
        const responseText = await response.text();
        const json = responseText.trim().length > 0 && JSON.parse(responseText);

        if (response.ok) {
            return json;
        } else {
            return { error: json.error || 'Failed to fetch radio.' };
        }
    } catch (error) {
        console.error('getRadio:', error);

        return error;
    }
};

// MARK: getAllRadios
export const getAllRadios = async (token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/manage/radios`, payload);
        const responseText = await response.text();
        const json = responseText.trim().length > 0 && JSON.parse(responseText);

        if (response.ok) {
            return json;
        } else {
            return { error: json.error || 'Failed to fetch radios.' };
        }
    } catch (error) {
        console.error('getAllRadios:', error);

        return error;
    }
};

// MARK: addRadio
export const addRadio = async (radio, token) => {
    try {
        const payload = {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'Content-Type': 'application/json',
                'x-patrol-id': _patrolId
            },
            body: JSON.stringify(radio)
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/manage/radios`, payload);
        const responseText = await response.text();
        const json = responseText.trim().length > 0 && JSON.parse(responseText);

        if (response.ok) {
            const location = response.headers.get('Location');
            const id = location.split('/').pop();

            return { id };
        } else {
            return { error: json.error || 'Failed to add radio.' };
        }
    } catch (error) {
        console.error('addRadio:', error);

        return error;
    }
};

// MARK: updateRadio
export const updateRadio = async (radio, token) => {
    try {
        const payload = {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'Content-Type': 'application/json',
                'x-patrol-id': _patrolId
            },
            body: JSON.stringify(radio)
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/manage/radios/${ radio.id }`, payload);
        const responseText = await response.text();
        const json = responseText.trim().length > 0 && JSON.parse(responseText);

        if (response.ok) {
            const location = response.headers.get('Location');
            const id = location.split('/').pop();

            return { id };
        } else {
            return { error: json.error };
        }
    } catch (error) {
        console.error('addRadio:', error);

        return error;
    }
};

// MARK: removeRadio
export const removeRadio = async (id, token) => {
    try {
        const payload = {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'Content-Type': 'application/json',
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/manage/radios/${ id }`, payload);

        if (response.ok) {
            return true;
        } else {
            const json = await response.json();

            return { error: json.error };
        }
    } catch (error) {
        console.error('removeRadio:', error);

        return error;
    }
};

// MARK: uploadPhoto
export const uploadPhoto = async (id, photo, token) => {
    try {
        const formData = new FormData();

        formData.append('id', id);
        formData.append('photo', photo);

        const payload = {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'x-patrol-id': _patrolId
            },
            body: formData
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/manage/patrollers/photos`, payload);

        if (response.ok) {
            return true;
        } else {
            return;
        }
    } catch (error) {
        console.error('uploadPhoto:', error);

        return error;
    }
};

// MARK: getRoster
export const getRoster = async (id, token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'x-patrol-id': _patrolId
            }
        };

        const response = id && await fetch(`${ apiEndpoint }/api/v1/schedule/rosters/${ id }`, payload);

        if (response.ok) {
            return await response.json();
        } else {
            return;
        }
    } catch (error) {
        console.error('getRoster:', error);

        return error;
    }
};

// MARK: getRole
export const getRole = async (id, token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/schedule/roles/${ id }`, payload);

        if (response.ok) {
            const json = await response.json();

            return json;
        } else if (response.status === 401) {
            return { error: 'Your session has expired or is invalid. Please log out and log back in to continue.' };
        } else {
            return;
        }
    } catch (error) {
        console.error('getRole:', error);

        return null;
    }
};

// MARK: HC-sendNotification
export const sendNotification = async (messageType, token) => {
    try {
        const payload = {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'Content-Type': 'application/json',
                'x-patrol-id': _patrolId
            },
            body: JSON.stringify({ type: messageType })
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/manage/notifications`, payload);
        const json = response && await response.json();

        if (json?.error) {
            return { error: json.error || 'Failed to send notification.' };
        } else {
            return json;
        }
    } catch (error) {
        console.error('sendNotification:', error);

        return null;
    }
};

// MARK: HC-getAlert
export const getAlertEvent = async (id, token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/notify/alerts/${ id }`, payload);
        const responseText = await response.text();
        const json = responseText.trim().length > 0 && JSON.parse(responseText);

        if (json?.error) {
            return { error: json.error || 'Failed to fetch alert events.' };
        } else if (response.ok && json) {
            return json;
        } else {
            return null;
        }
    } catch (error) {
        console.error('getAlertEvents:', error);

        return null;
    }
};

// MARK: HC-getAlerts
export const getAllAlertEvents = async (token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/notify/alerts`, payload);
        const responseText = await response.text();
        const json = responseText.trim().length > 0 && JSON.parse(responseText);

        if (response.ok) {
            return json;
        } else {
            if (json && json?.error) {
                return { error: json.error || 'Failed to fetch alert events.' };
            }

            return [];
        }
    } catch (error) {
        console.error('getAlertEvents:', error);

        return [];
    }
};

// MARK: HC-openAlertEvent
export const openAlertEvent = async (type, shiftIds, token) => {
    try {
        const body = { type };

        if (Array.isArray(shiftIds) && shiftIds.length > 0) {
            body.shiftIds = shiftIds;
        }

        const payload = {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'Content-Type': 'application/json',
                'x-patrol-id': _patrolId
            },
            body: JSON.stringify(body)
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/notify/alerts`, payload);
        const responseText = await response.text();
        const json = responseText.trim().length > 0 && JSON.parse(responseText);

        if (response.ok) {
            return json;
        } else {
            if (json && json?.error) {
                return { error: json.error || `open-failed-${ response.status }` };
            }

            return { error: `open-failed-${ response.status }` };
        }
    } catch (error) {
        console.error('openAlertEvent:', error);

        return { error: error.message || 'Failed to open alert event.' };
    }
};

// MARK: HC-closeAlertEvent
export const closeAlertEvent = async (id, token) => {
    try {
        const payload = {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/notify/alerts/${ id }`, payload);
        const responseText = await response.text();
        const json = responseText.trim().length > 0 && JSON.parse(responseText);

        if (response.ok) {
            return true;
        } else {
            if (json && json?.error) {
                return { error: json.error || `close-failed-${ response.status }` };
            }

            return { error: `close-failed-${ response.status }` };
        }
    } catch (error) {
        console.error('closeAlertEvent:', error);

        return { error: error.message || 'Failed to close alert event.' };
    }
};

// MARK: HC-getReport
export const getReport = async (reportType, shiftId, token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/manage/reports?type=${ reportType }&shift=${ shiftId }`, payload);
        const responseText = await response.text();
        const json = responseText.trim().length > 0 && JSON.parse(responseText);

        if (response.ok) {
            return json;
        } else {
            return null;
        }
    } catch (error) {
        console.error('getReport:', error);

        return null;
    }
};

// MARK: HC-getEquipmentByType
export const getEquipmentByType = async (equipmentType, token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/manage/equipment?type=${ equipmentType }`, payload);
        const responseText = await response.text();
        const json = responseText.trim().length > 0 && JSON.parse(responseText);

        if (response.ok) {
            return json;
        } else {
            return null;
        }
    } catch (error) {
        console.error('getEquipmentByType:', error);

        return null;
    }
};

// MARK: HC-updateEquipment
export const updateEquipment = async (equipment, token) => {
    try {
        const payload = {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'Content-Type': 'application/json',
                'x-patrol-id': _patrolId
            },
            body: JSON.stringify(equipment)
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/manage/equipment`, payload);
        const responseText = await response.text();
        const json = responseText.trim().length > 0 && JSON.parse(responseText);

        if (response.ok) {
            return json;
        } else {
            return { error: json.error };
        }
    } catch (error) {
        console.error('updateEquipment:', error);

        throw error;
    }
};

// MARK: HC-removeEquipment
export const removeEquipment = async (id, token) => {
    try {
        const response = await fetch(`${ apiEndpoint }/api/v1/manage/equipment/${ id }`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${ token }`, 'x-patrol-id': _patrolId }
        });

        return response.ok;
    } catch (error) {
        console.error('removeEquipment:', error);

        return false;
    }
};

// MARK: HC-postReport
export const postReport = async (name, shiftId, data, token) => {
    try {
        const payload = {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'Content-Type': 'application/json',
                'x-patrol-id': _patrolId
            },
            body: JSON.stringify(data)
        };

        const response = await fetch(
            `${ apiEndpoint }/api/v1/manage/reports/${ encodeURIComponent(name) }/shift/${ encodeURIComponent(shiftId) }`,
            payload
        );
        const responseText = await response.text();
        const json = responseText.trim().length > 0 && JSON.parse(responseText);

        if (response.ok) {
            const location = response.headers.get('Location');
            const id = location.split('/').pop();

            return { id };
        } else {
            return { error: json.error };
        }
    } catch (error) {
        console.error('postReport:', error);

        throw error;
    }
};

// MARK: getResortWeatherLocation
export const getResortWeatherLocation = () => {
    const lat = parseFloat(45.343782796177464);
    const lon = parseFloat(-94.30522541211948);

    return { lat, lon };
};

// MARK: getWeatherAlerts
export const getWeatherAlerts = async (lat, lon) => {
    try {
        // Get weather alerts from NWS
        const pointResponse = await fetch(
            `https://api.weather.gov/points/${ lat },${ lon }`
        );

        if (!pointResponse.ok) {
            throw new Error('Failed to get weather alerts information');
        }

        const pointData = await pointResponse.json();

        // Get active alerts for the area
        const alertsResponse = await fetch(pointData.properties.alerts);

        if (!alertsResponse.ok) {
            throw new Error('Failed to fetch weather alerts data');
        }

        const alertsData = await alertsResponse.json();

        return alertsData.features.map(alert => ({
            title: alert.properties.headline,
            description: alert.properties.description,
            instruction: alert.properties.instruction,
            severity: alert.properties.severity,
            effective: alert.properties.effective,
            expires: alert.properties.expires,
            link: alert.properties.web
        }));
    } catch (error) {
        console.error('getWeatherAlerts:', error);

        throw error;
    }
};

// MARK: getCurrentWeather
export const getCurrentWeather = async (lat, lon) => {
    try {
        // First, get the weather station/grid point from NWS
        const pointResponse = await fetch(
            `https://api.weather.gov/points/${ lat },${ lon }`
        );

        if (!pointResponse.ok) {
            throw new Error('Failed to get weather station information');
        }

        const pointResponseText = await pointResponse.text();
        const pointData = pointResponseText.trim().length > 0 && JSON.parse(pointResponseText);

        if (!pointData.properties.observationStations) {
            throw new Error('No weather stations found in area');
        }

        // Get current observations from the nearest weather station
        const stationsResponse = await fetch(pointData.properties.observationStations);
        const stationsResponseText = await stationsResponse.text();
        const stationsData = stationsResponseText.trim().length > 0 && JSON.parse(stationsResponseText);

        if (!stationsData.features || stationsData.features.length === 0) {
            throw new Error('No weather stations found in area');
        }

        // Map NWS conditions to our weather options
        const mapNWSCondition = (description) => {
            if (!description) return 'Clear';

            const desc = description.toLowerCase();

            if (desc.includes('clear') || desc.includes('sunny')) return 'Clear';
            if (desc.includes('partly cloudy') || desc.includes('few clouds')) return 'Partly Cloudy';
            if (desc.includes('mostly cloudy') || desc.includes('scattered clouds')) return 'Partly Cloudy';
            if (desc.includes('overcast') || desc.includes('cloudy')) return 'Overcast';
            if (desc.includes('rain') || desc.includes('drizzle')) return 'Rain';
            if (desc.includes('light snow') || desc.includes('flurries')) return 'Light Snow';
            if (desc.includes('snow') || desc.includes('blizzard')) return 'Heavy Snow';
            if (desc.includes('fog') || desc.includes('mist') || desc.includes('haze')) return 'Fog';
            if (desc.includes('ice') || desc.includes('freezing')) return 'Ice';
            if (desc.includes('wind')) return 'Wind Hold';

            return 'Clear';
        };

        // Map wind speed to our wind conditions (NWS uses m/s, convert to kph)
        const getWindCondition = (windSpeedKMH) => {
            if (windSpeedKMH === null || windSpeedKMH === undefined) return 'Unknown';
            if (windSpeedKMH < 6.5) return 'Calm < 4MPH'; // <4 mph
            if (windSpeedKMH < 13) return 'Light 4-8MPH'; // <8 mph
            if (windSpeedKMH < 24) return 'Moderate 8-15MPH'; // <15 mph
            if (windSpeedKMH < 40) return 'Strong 15-25MPH'; // <25 mph

            return 'Extreme';
        };

        // Convert Celsius to Fahrenheit
        const celsiusToFahrenheit = (celsius) => {
            if (!celsius) return null;

            return Math.round((celsius * 9 / 5) + 32);
        };

        // Get weather for the first 4 stations, but use the first with valid data as mainWeather
        const stationWeather = [];

        let mainWeather = null;

        for (const station of stationsData.features.slice(0, 4)) {
            try {
                const obsResponse = await fetch(`${ station.id }/observations/latest`);

                if (obsResponse.ok) {
                    const obsData = await obsResponse.json();
                    const props = obsData.properties;
                    const weatherObj = {
                        name: station.properties.name,
                        stationId: station.properties.stationIdentifier,
                        temperature: celsiusToFahrenheit(props.temperature?.value),
                        conditions: mapNWSCondition(props.textDescription),
                        wind: getWindCondition(props.windSpeed?.value),
                        description: props.textDescription || 'No description available',
                        windSpeed: props.windSpeed?.value ? Math.round(props.windSpeed.value * 3.6) : 0, // kph
                        windDirection: props.windDirection?.value ? Math.round(props.windDirection.value) : null, // degrees
                        windGust: props.windGust?.value ? Math.round(props.windGust.value * 3.6) : 0, // kph
                        windChill: props.windChill?.value ? celsiusToFahrenheit(props.windChill.value) : null,
                        humidity: props.relativeHumidity?.value ? Math.round(props.relativeHumidity.value) : null,
                        visibility: props.visibility?.value ? Math.round(props.visibility.value * 0.000621371) : null, // meters to miles
                        station: obsData.properties.station,
                        timestamp: props.timestamp
                    };

                    if (!mainWeather) {
                        mainWeather = weatherObj;
                    } else {
                        stationWeather.push(weatherObj);
                    }
                }
            } catch (err) {
                console.warn('Failed to get data from station:', station.id);

                continue;
            }
        }
        if (!mainWeather) {
            throw new Error('No current weather data available');
        }
        return {
            ...mainWeather,
            nearbyStations: stationWeather.slice(0, 3)
        };
    } catch (error) {
        console.error('getCurrentWeather:', error);

        throw error;
    }
};

// MARK: getWeatherRadar
export const getWeatherRadar = async (lat, lon) => {
    try {
        // Using NWS radar imagery
        // First get the radar station for the area
        const pointResponse = await fetch(
            `https://api.weather.gov/points/${ lat },${ lon }`
        );

        if (!pointResponse.ok) {
            throw new Error('Failed to get radar station information');
        }

        const pointResponseText = await pointResponse.text();
        const pointData = pointResponseText.trim().length > 0 && JSON.parse(pointResponseText);

        const radarStation = pointData.properties.radarStation;

        if (!radarStation) {
            throw new Error('No radar station available for this location');
        }

        // NWS radar URLs
        const baseRadarUrl = `https://radar.weather.gov/ridge/standard/${ radarStation }_loop.gif`;

        return {
            radarUrl: baseRadarUrl,
            mapUrl: `https://radar.weather.gov/?settings=v1_eyJhZ2VuZGEiOnsiaWQiOm51bGx9LCJhbmltYXRpbmciOmZhbHNlLCJiYXNlIjoic3RhbmRhcmQiLCJhcnRjYyI6ZmFsc2UsImNvdW50eSI6ZmFsc2UsImN3YSI6ZmFsc2UsImZvY3VzIjpudWxsLCJtZW51Ijp0cnVlLCJtb3NhaWMiOmZhbHNlLCJtcWYiOmZhbHNlLCJxdWFsaXR5IjoyLCJyZmMiOmZhbHNlLCJzaG91c2UiOnRydWUsInNvdW5kaW5nIjpmYWxzZSwic3RhdGUiOmZhbHNlLCJzdGF0aW9uIjoidCIsInN0YXRpb25zIjpmYWxzZSwieCI6IiR7bG9ufSIsInkiOiIke2xhdH0iLCJ6b29tIjoke3pvb219fQ%3D%3D#/`,
            station: radarStation
        };
    } catch (error) {
        console.error('getWeatherRadar:', error);
        throw error;
    }
};

// MARK: getWeatherForecast
export const getWeatherForecast = async (lat, lon) => {
    try {
        // Get the forecast from NWS
        const pointResponse = await fetch(
            `https://api.weather.gov/points/${ lat },${ lon }`
        );

        if (!pointResponse.ok) {
            throw new Error('Failed to get forecast information');
        }

        const pointResponseText = await pointResponse.text();
        const pointData = pointResponseText.trim().length > 0 && JSON.parse(pointResponseText);

        // Get the 7-day forecast
        const forecastResponse = await fetch(pointData.properties.forecast);

        if (!forecastResponse.ok) {
            throw new Error('Failed to fetch forecast data');
        }

        const forecastResponseText = await forecastResponse.text();
        const forecastData = forecastResponseText.trim().length > 0 && JSON.parse(forecastResponseText);

        // Process the forecast periods into daily summaries
        const dailyForecasts = [];
        const periods = forecastData.properties.periods;

        // Group periods by day (day and night periods)
        for (let i = 0; i < periods.length && dailyForecasts.length < 5; i += 2) {
            const dayPeriod = periods[ i ];
            const nightPeriod = periods[ i + 1 ];

            if (dayPeriod) {
                const date = new Date(dayPeriod.startTime).toISOString().split('T')[ 0 ];

                dailyForecasts.push({
                    date,
                    name: dayPeriod.name,
                    highTemp: dayPeriod.temperature,
                    lowTemp: nightPeriod ? nightPeriod.temperature : null,
                    conditions: dayPeriod.shortForecast,
                    detailedForecast: dayPeriod.detailedForecast,
                    windSpeed: dayPeriod.windSpeed,
                    windDirection: dayPeriod.windDirection,
                    icon: dayPeriod.icon
                });
            }
        }

        return dailyForecasts;
    } catch (error) {
        console.error('getWeatherForecast:', error);

        throw error;
    }
};

// MARK: GitHub
export const callSubmitGitHubIssue = async (email, type, desc, token) => {
    try {
        const payload = {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'Content-Type': 'application/json',
                'X-System-Source': 'patrol360-manager',
                'x-patrol-id': _patrolId
            },
            body: JSON.stringify(
                {
                    email: email,
                    desc: desc,
                    type: type
                }
            )
        };

        const response = await fetch(`${ apiEndpoint }/api/submit-github-issue`, payload);
        const responseText = await response.text();
        const json = responseText.trim().length > 0 && JSON.parse(responseText);

        if (response.ok) {
            return true;
        } else {
            return { error: json.error || 'Failed to submit GitHub issue.' };
        }
    } catch (error) {
        console.error('callSubmitGitHubIssue:', error);

        return { error: error.message };
    }
};